import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";

import type { WizardPartner } from "../useCreateProgramWizard";

import { Button as ButtonV2 } from "../../../../Button";
import { InlineNotification } from "../../../../InlineNotification";
import { InputV2 } from "../../../../InputV2";
import { Toggle } from "../../../../Toggle";
import { Typo } from "../../../../Typo";
import { COMMENT_CHARACTER_LIMIT } from "../../../consts";
import { WizardFooter } from "../WizardFooter";

type RelationshipStepProps = {
  title: string;
  stepLabel?: string;
  partner: WizardPartner;
  onChange: (
    patch: Partial<Pick<WizardPartner, "identityToggle" | "comment">>,
  ) => void;
  onBack: () => void;
  onContinue: () => void;
  isMobile?: boolean;
  /** The Comment field is Phase-2 (part-4039-master-sub-ib-phase-2) — hidden while the flag is off. */
  showComment?: boolean;
  /** True only when this step repeats (2 Sub IBs), which is when the canvas names the partner. */
  namePartner?: boolean;
  footerContainer?: Element | null;
};

export const RelationshipStep = ({
  title,
  stepLabel,
  partner,
  onChange,
  onBack,
  onContinue,
  isMobile,
  showComment,
  namePartner,
  footerContainer,
}: RelationshipStepProps) => {
  const { t } = useTranslation();

  return (
    <div className="flex min-h-0 flex-1 flex-col gap-6">
      <div className="flex flex-col gap-3">
        {stepLabel && (
          <Typo
            element="span"
            type="labelXS"
            textColor="text-text-tertiary"
            className="uppercase tracking-[0.04em]"
          >
            {stepLabel}
          </Typo>
        )}
        <Typo element="h2" type={isMobile ? "headingLG" : "headingXL"}>
          {title}
        </Typo>
        <Typo element="p" type="bodySM" textColor="text-text-secondary">
          {/* Named only when the step repeats, mirroring the canvas's
              `subtitleOverride={twoPartner ? … : undefined}` — with one Sub IB
              there is nothing to disambiguate, so the generic wording stands.

              There is deliberately no new-vs-existing variant. The 14 Jul 2026
              requirement gave an existing Sub IB its own reminder subtitle
              ("Editing will update existing settings of this Sub IB…"); the
              canvas dropped that on 07 Aug 2026 ("no new-vs-existing copy
              distinction — every partner sees the same subtitle"), and the
              finalized copy carries no key for it. */}
          {namePartner
            ? t(
                "subIBReport_relationshipSubtitleNamed",
                "Control what's shared between {{name}} and you.",
                { name: partner.displayName },
              )
            : t(
                "subIBReport_relationshipSubtitle",
                "Control what's shared between your Sub IB and you.",
              )}
        </Typo>
      </div>

      <div className="flex items-start justify-between gap-4">
        <div className="flex min-w-0 flex-col gap-1">
          <Typo element="span" type="labelSM" textColor="text-text-primary">
            {t("subIBReport_permIdentityTitle", "Show name and email")}
          </Typo>
          <Typo
            element="span"
            type="bodyXS"
            textColor="text-text-secondary"
            className="max-w-[340px]"
          >
            {t(
              "subIBReport_permIdentityHelper",
              "What the Sub IB can see about you",
            )}
          </Typo>
        </div>
        <div className="flex shrink-0 items-center gap-3">
          <Toggle
            checked={partner.identityToggle}
            onChange={(checked) => onChange({ identityToggle: checked })}
            ariaLabel={t(
              "subIBReport_permIdentityTitle",
              "Show name and email",
            )}
          />
          <Typo
            element="span"
            type="bodySM"
            textColor="text-text-primary"
            className="min-w-[22px] text-right"
          >
            {partner.identityToggle
              ? t("common_on", "On")
              : t("common_off", "Off")}
          </Typo>
        </div>
      </div>

      {/* Design change #1 (PART-3930): single-line InputV2 at 60 chars. The
          guidance copy moves out of the placeholder into a subLabel so it stays
          visible once typing starts, and the field starts empty. InputV2 renders
          the in-field N/60 counter itself — do not add another. */}
      {/* Label block built here rather than via InputV2's `label`/`subLabel`:
          the canvas renders "(optional)" as its own muted, regular-weight span
          beside the label (`wiz-field__optional`, text-tertiary/400), which a
          single label string cannot express — it came out at label weight. */}
      {showComment && (
        <div className="flex flex-col gap-2">
          <div className="flex flex-col gap-1">
            <label
              htmlFor="wizard-relationship-comment"
              className="inline-flex flex-wrap items-baseline gap-1"
            >
              <Typo element="span" type="labelSM" textColor="text-text-primary">
                {t("subIBReport_commentLabel", "Note")}
              </Typo>
              <Typo element="span" type="bodyXS" textColor="text-text-tertiary">
                {t("subIBReport_commentOptional", "(optional)")}
              </Typo>
            </label>
            <Typo element="span" type="bodyXS" textColor="text-text-secondary">
              {t(
                "subIBReport_commentSubLabel",
                "Add context for your reference about this Sub IB. Your Sub IB won't see this.",
              )}
            </Typo>
          </div>
          <InputV2
            name="wizard-relationship-comment"
            value={partner.comment}
            characterLimit={COMMENT_CHARACTER_LIMIT}
            onChange={(value) =>
              onChange({ comment: value.slice(0, COMMENT_CHARACTER_LIMIT) })
            }
          />
        </div>
      )}

      <InlineNotification variant="info">
        {t(
          "subIBReport_relationshipNotice",
          "Your Sub IB won't see the rebate split, your earnings, or your shared client reports.",
        )}
      </InlineNotification>

      {/* TODO: remove IIFE per "No IIFEs in JSX" (AGENTS.md) — pick basic/medium/complex tier */}
      {(() => {
        const footer = (
          <WizardFooter isMobile={isMobile}>
            <ButtonV2 variant="tonal" size="md" onClick={onBack}>
              {t("common_back", "Back")}
            </ButtonV2>
            <ButtonV2 variant="brand" size="md" onClick={onContinue}>
              {t("common_next", "Next")}
            </ButtonV2>
          </WizardFooter>
        );
        return footerContainer ? createPortal(footer, footerContainer) : footer;
      })()}
    </div>
  );
};
